import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
    FaHeart,
    FaRegHeart,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt,
    FaRegStar,
    FaArrowRight,
} from "react-icons/fa";

export default function TopRatedHotels({ limit = 4 }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);

    const navigate = useNavigate();

    /* =========================================================
       WISHLIST
    ========================================================= */

    useEffect(() => {
        const storedWishlist =
            JSON.parse(localStorage.getItem("wishlist")) || [];

        setWishlist(storedWishlist);
    }, []);

    const updateWishlist = (updated) => {
        setWishlist(updated);

        localStorage.setItem(
            "wishlist",
            JSON.stringify(updated)
        );
    };

    const toggleWishlist = (hotel) => {
        const isLoggedIn = true;

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(
            (item) => item.id === hotel.id
        );

        let updated;

        if (exists) {
            updated = wishlist.filter(
                (item) => item.id !== hotel.id
            );
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(
            (item) => item.id === id
        );
    };

    /* =========================================================
       FETCH TOP RATED HOTELS
    ========================================================= */

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get(
                    "https://cheapdubaihotels.com/api/top-rated-hotels"
                );

                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    /* =========================================================
       STARS
    ========================================================= */

    const renderStars = (rating) => {
        const stars = [];
        const safeRating = Number(rating) || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(
                    <FaStar
                        key={i}
                        className="text-[#F7B928]"
                    />
                );
            } else if (
                i === Math.floor(safeRating) &&
                safeRating % 1 >= 0.5
            ) {
                stars.push(
                    <FaStarHalfAlt
                        key={i}
                        className="text-[#F7B928]"
                    />
                );
            } else {
                stars.push(
                    <FaRegStar
                        key={i}
                        className="text-[#D7E1E6]"
                    />
                );
            }
        }

        return stars;
    };

    /* =========================================================
       DISCOUNT
    ========================================================= */

    const getDiscount = (hotel) => {
        if (hotel.discount) {
            return hotel.discount;
        }

        if (hotel.discount_percentage) {
            return hotel.discount_percentage;
        }

        return null;
    };

    /* =========================================================
       LOADING
    ========================================================= */

    if (loading) {
        return (
            <section className="w-full bg-[#F7FAFC] px-4 py-14 sm:px-6 lg:px-10 lg:py-18">
                <div className="mx-auto w-full max-w-[1536px]">

                    {/* HEADER SKELETON */}

                    <div className="mb-9 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">

                        <div className="space-y-3">
                            <div className="h-3 w-28 animate-pulse rounded-full bg-[#DCE7ED]" />

                            <div className="h-9 w-72 animate-pulse rounded-lg bg-[#DCE7ED]" />

                            <div className="h-3 w-[380px] max-w-full animate-pulse rounded-full bg-[#DCE7ED]" />
                        </div>

                        <div className="h-11 w-36 animate-pulse rounded-xl bg-[#DCE7ED]" />
                    </div>

                    {/* CARD SKELETONS */}

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
                        {[1, 2, 3, 4].map((item) => (
                            <div
                                key={item}
                                className="overflow-hidden rounded-2xl border border-[#E1EAF0] bg-white"
                            >
                                <div className="h-[225px] animate-pulse bg-[#DCE7ED]" />

                                <div className="space-y-4 p-5">
                                    <div className="h-5 animate-pulse rounded bg-[#DCE7ED]" />

                                    <div className="h-3 w-2/3 animate-pulse rounded bg-[#DCE7ED]" />

                                    <div className="h-6 w-1/2 animate-pulse rounded bg-[#DCE7ED]" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    /* =========================================================
       RENDER
    ========================================================= */

    return (
        <section className="relative w-full  max-w-[1536px] mx-auto
              px-4
            sm:px-6
            lg:px-8 overflow-hidden bg-[#F7FAFC] pb-14 pt-14 sm:pb-16 lg:pb-20 lg:pt-16">

            {/* =====================================================
                SOFT BACKGROUND DECORATION
            ===================================================== */}

            <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full border border-[#0B6E8E]/[0.07]" />

            <div className="pointer-events-none absolute -left-48 bottom-0 h-[460px] w-[460px] rounded-full border border-[#F7B928]/[0.10]" />

            <div className="pointer-events-none absolute right-[12%] top-24 h-2 w-2 rounded-full bg-[#F7B928]" />

            <div className="relative z-10 mx-auto w-full max-w-[1536px]">

                {/* =================================================
                    SECTION HEADER
                ================================================= */}

                <div className="mb-9 flex flex-col gap-6 lg:mb-10 lg:flex-row lg:items-end lg:justify-between">

                    <div className="max-w-[680px]">

                        {/* EYEBROW */}

                        <div className="mb-3 flex items-center gap-2.5">
                            <span className="h-[2px] w-7 bg-[#FF5A1F]" />

                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B6E8E]">
                                Featured Hotels
                            </span>
                        </div>

                        {/* HEADING */}

                        <h2 className="text-[30px] font-extrabold leading-[1.08] tracking-[-0.035em] text-[#062F4F] sm:text-[36px] md:text-[42px]">
                            Top Rated Hotels{" "}
                            <span className="text-[#0B6E8E]">
                                in Dubai
                            </span>
                        </h2>

                        {/* DESCRIPTION */}

                        <p className="mt-3 max-w-[620px] text-[13px] leading-[1.7] text-[#667B86] sm:text-[14px]">
                            Discover highly rated hotels selected for
                            comfort, location, value and a better Dubai
                            stay.
                        </p>
                    </div>

                    {/* VIEW ALL */}

                    <button
                        type="button"
                        onClick={() => navigate("/hotels")}
                        className="
                            group
                            inline-flex
                            w-fit
                            shrink-0
                            items-center
                            gap-2.5
                            rounded-xl
                            bg-[#062F4F]
                            px-5
                            py-3
                            text-[11px]
                            font-bold
                            uppercase
                            tracking-[0.07em]
                            text-white
                            shadow-[0_8px_22px_rgba(6,47,79,0.15)]
                            transition-all
                            duration-300
                            hover:-translate-y-0.5
                            hover:bg-[#0B6E8E]
                            hover:shadow-[0_12px_28px_rgba(6,47,79,0.20)]
                            active:scale-[0.98]
                        "
                    >
                        <span>View All Hotels</span>

                        <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </div>

                {/* =================================================
                    HOTEL GRID
                ================================================= */}

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">

                    {hotels.slice(0, limit).map((hotel, index) => {
                        const discount = getDiscount(hotel);

                        return (
                            <article
                                key={hotel.id}
                                className="
                                    group
                                    relative
                                    overflow-hidden
                                    rounded-2xl
                                    border
                                    border-[#DFE8ED]
                                    bg-white
                                    shadow-[0_6px_25px_rgba(6,47,79,0.06)]
                                    transition-all
                                    duration-300
                                    hover:-translate-y-1.5
                                    hover:border-[#C9DCE5]
                                    hover:shadow-[0_18px_40px_rgba(6,47,79,0.13)]
                                "
                            >

                                {/* =================================================
                                    IMAGE
                                ================================================= */}

                                <div
                                    className="
                                        relative
                                        h-[225px]
                                        cursor-pointer
                                        overflow-hidden
                                        sm:h-[215px]
                                        lg:h-[220px]
                                    "
                                    onClick={() =>
                                        navigate(
                                            `/hotels/${hotel.slug}`
                                        )
                                    }
                                >

                                    <img
                                        src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                        alt={hotel.name}
                                        className="
                                            h-full
                                            w-full
                                            object-cover
                                            transition-transform
                                            duration-700
                                            ease-out
                                            group-hover:scale-[1.06]
                                        "
                                    />

                                    {/* IMAGE GRADIENT */}

                                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#062F4F]/70 via-[#062F4F]/10 to-transparent" />

                                    {/* TOP RATED */}

                                    {index === 0 && (
                                        <div className="absolute left-3.5 top-3.5 flex items-center gap-1.5 rounded-lg bg-[#F7B928] px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-[0.07em] text-[#062F4F] shadow-[0_5px_16px_rgba(0,0,0,0.15)]">
                                            <FaStar className="text-[9px]" />
                                            Top Rated
                                        </div>
                                    )}

                                    {/* DISCOUNT */}

                                    {discount && (
                                        <div className="absolute bottom-3.5 left-3.5 rounded-lg bg-[#FF5A1F] px-3 py-1.5 text-[10px] font-extrabold text-white shadow-[0_5px_16px_rgba(255,90,31,0.25)]">
                                            {String(discount).includes("%")
                                                ? `-${String(discount).replace("%", "")}% OFF`
                                                : `-${discount}% OFF`}
                                        </div>
                                    )}

                                    {/* WISHLIST */}

                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleWishlist(hotel);
                                        }}
                                        className="
                                            absolute
                                            right-3.5
                                            top-3.5
                                            flex
                                            h-9
                                            w-9
                                            items-center
                                            justify-center
                                            rounded-full
                                            bg-white/95
                                            text-[#062F4F]
                                            shadow-[0_5px_18px_rgba(0,0,0,0.14)]
                                            backdrop-blur-sm
                                            transition-all
                                            duration-300
                                            hover:scale-110
                                            hover:bg-white
                                        "
                                        aria-label={
                                            isWishlisted(hotel.id)
                                                ? "Remove from wishlist"
                                                : "Add to wishlist"
                                        }
                                    >
                                        {isWishlisted(hotel.id) ? (
                                            <FaHeart className="text-[14px] text-[#FF5A1F]" />
                                        ) : (
                                            <FaRegHeart className="text-[14px] text-[#062F4F]" />
                                        )}
                                    </button>

                                    {/* LOCATION ON IMAGE */}

                                    <div className="absolute bottom-3.5 right-3.5 max-w-[55%]">
                                        <div className="truncate rounded-lg bg-white/95 px-2.5 py-1.5 text-[9px] font-bold text-[#062F4F] shadow-md backdrop-blur-sm">
                                            {hotel.destination ||
                                                hotel.location ||
                                                hotel.city ||
                                                "Dubai"}
                                        </div>
                                    </div>
                                </div>

                                {/* =================================================
                                    CONTENT
                                ================================================= */}

                                <div className="p-5 sm:p-5.5">

                                    {/* HOTEL NAME */}

                                    <h3
                                        onClick={() =>
                                            navigate(
                                                `/hotels/${hotel.slug}`
                                            )
                                        }
                                        title={hotel.name}
                                        className="
                                            cursor-pointer
                                            truncate
                                            text-[17px]
                                            font-bold
                                            leading-tight
                                            text-[#062F4F]
                                            transition-colors
                                            duration-200
                                            hover:text-[#0B6E8E]
                                        "
                                    >
                                        {hotel.name}
                                    </h3>

                                    {/* LOCATION */}

                                    <div className="mt-2.5 flex min-w-0 items-center gap-2">
                                        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EAF5F8]">
                                            <FaMapMarkerAlt className="text-[10px] text-[#0B6E8E]" />
                                        </div>

                                        <span
                                            title={hotel.location}
                                            className="truncate text-[11px] font-medium text-[#71838D]"
                                        >
                                            {hotel.location ||
                                                hotel.destination ||
                                                hotel.city ||
                                                "Dubai"}
                                        </span>
                                    </div>

                                    {/* DIVIDER */}

                                    <div className="my-3.5 h-px w-full bg-[#E7EEF2]" />

                                    {/* RATING */}

                                    <div className="flex items-center justify-between">

                                        <div className="flex items-center gap-2">

                                            <div className="flex items-center gap-[2px] text-[10px]">
                                                {renderStars(
                                                    hotel.rating
                                                )}
                                            </div>

                                            <span className="text-[11px] font-extrabold text-[#062F4F]">
                                                {hotel.rating || "0"}
                                            </span>
                                        </div>

                                        <span className="text-[9px] font-semibold uppercase tracking-[0.06em] text-[#91A1A9]">
                                            Guest Rating
                                        </span>
                                    </div>

                                    {/* PRICE */}

                                    <div className="mt-4 flex items-end justify-between gap-3">

                                        <div>
                                            <span className="block text-[9px] font-semibold uppercase tracking-[0.1em] text-[#8A9AA2]">
                                                Starting from
                                            </span>

                                            <div className="mt-1 flex items-baseline gap-1.5">

                                                <span className="text-[20px] font-extrabold leading-none text-[#062F4F]">
                                                    AED{" "}
                                                    {Math.round(
                                                        hotel.price_per_night
                                                    )}
                                                </span>

                                                <span className="text-[10px] text-[#7D8D95]">
                                                    / night
                                                </span>
                                            </div>
                                        </div>

                                        {/* DETAILS BUTTON */}

                                        <button
                                            type="button"
                                            onClick={() =>
                                                navigate(
                                                    `/hotels/${hotel.slug}`
                                                )
                                            }
                                            className="
                                                group/arrow
                                                flex
                                                h-10
                                                w-10
                                                shrink-0
                                                items-center
                                                justify-center
                                                rounded-xl
                                                bg-[#FF5A1F]
                                                text-white
                                                shadow-[0_6px_16px_rgba(255,90,31,0.20)]
                                                transition-all
                                                duration-300
                                                hover:bg-[#F04F17]
                                                hover:shadow-[0_9px_22px_rgba(255,90,31,0.28)]
                                            "
                                            aria-label={`View ${hotel.name}`}
                                        >
                                            <FaArrowRight className="text-[11px] transition-transform duration-300 group-hover/arrow:translate-x-0.5" />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>

                {/* =================================================
                    TRUST STRIP
                ================================================= */}

                <div className="mt-9 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5 border-t border-[#DDE7EC] pt-6 text-center">

                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#71838D]">
                        Verified Hotels
                    </span>

                    <span className="h-1 w-1 rounded-full bg-[#F7B928]" />

                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#71838D]">
                        Best Hotel Rates
                    </span>

                    <span className="h-1 w-1 rounded-full bg-[#F7B928]" />

                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#71838D]">
                        Local Dubai Deals
                    </span>

                    <span className="h-1 w-1 rounded-full bg-[#F7B928]" />

                    <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#71838D]">
                        Secure Booking
                    </span>
                </div>
            </div>
        </section>
    );
}